import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { FELDER_NICHT_LESBAR, felderMit, readFields, seriendruckFeldnamen } from "./felder";

export const lektionSeriendruckfelder: Lesson = {
  id: "seriendruckfelder",
  title: "Seriendruckfelder, Adressblock und Grußzeile",
  summary: "Die Stellen markieren, an denen jeder Brief persönlich wird.",
  durationMinutes: 10,
  learningGoals: [
    "Einzelne Seriendruckfelder einfügen",
    "Adressblock und Grußzeile als fertige Bausteine nutzen",
    "Das Ergebnis in der Vorschau prüfen",
  ],
  steps: [
    {
      id: "felder-einfuegen",
      title: "Einzelne Felder einfügen",
      explanation: [
        "„Sendungen“ → „Seriendruckfeld einfügen“ listet alle Spalten der Datenquelle. Ein " +
          "Klick setzt das Feld an die Cursorposition; im Text erscheint es als «Vorname». " +
          "Die spitzen Klammern sind nicht getippt – wer sie von Hand schreibt, bekommt " +
          "keinen Seriendruck, sondern Text mit Klammern.",
        "Leerzeichen und Satzzeichen zwischen den Feldern setzt du selbst: " +
          "«Vorname» «Nachname». Formatierung am Feld gilt für jeden eingesetzten Wert.",
      ],
      keyPoints: [
        "Sendungen → Seriendruckfeld einfügen.",
        "«Feld»-Klammern nie tippen.",
        "Leerzeichen zwischen Feldern selbst setzen.",
      ],
      task: {
        instruction: "Füge mindestens zwei verschiedene Seriendruckfelder in deinen Brief ein, etwa Vorname und Ort.",
        hint: "Cursor an die Stelle, Sendungen → Seriendruckfeld einfügen → Feld wählen.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) return FELDER_NICHT_LESBAR;
          const namen = seriendruckFeldnamen(felder);
          return fromCriteria(
            [
              criterion(
                `Mindestens zwei verschiedene Seriendruckfelder (gefunden: ${namen.join(", ") || "keine"})`,
                namen.length >= 2,
              ),
            ],
            {
              success: "Die Felder sitzen – jeder Brief bekommt seine eigenen Angaben.",
              failure: "Es fehlen noch Seriendruckfelder.",
              hint: "Getippte «Klammern» zählen nicht; das Feld muss über die Schaltfläche eingefügt sein.",
            },
          );
        },
      },
    },
    {
      id: "adressblock-gruss",
      title: "Adressblock und Grußzeile",
      explanation: [
        "Für die zwei häufigsten Stellen gibt es fertige Bausteine. Der Adressblock " +
          "setzt Name, Straße, PLZ und Ort in der richtigen Reihenfolge und lässt leere " +
          "Zeilen weg – etwa wenn ein Empfänger keinen Firmennamen hat. Die Grußzeile " +
          "baut aus Anrede und Nachname „Sehr geehrte Frau Weber,“ und hat einen Ersatztext " +
          "für Einträge ohne Namen.",
        "Beide Dialoge haben eine Schaltfläche „Felder zuordnen“. Heißen die Spalten der " +
          "Liste anders als Word erwartet, etwa „Name“ statt „Nachname“, ordnest du sie dort " +
          "einmal zu.",
      ],
      keyPoints: [
        "Adressblock lässt leere Zeilen weg.",
        "Grußzeile mit Ersatztext für Einträge ohne Namen.",
        "„Felder zuordnen“, wenn die Spaltennamen abweichen.",
      ],
      task: {
        instruction: "Füge einen Adressblock und eine Grußzeile in deinen Brief ein.",
        hint: "Sendungen → Adressblock bzw. Grußzeile, jeweils mit OK bestätigen.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) return FELDER_NICHT_LESBAR;
          return fromCriteria(
            [
              criterion("Adressblock eingefügt", felderMit(felder, "ADDRESSBLOCK").length > 0),
              criterion("Grußzeile eingefügt", felderMit(felder, "GREETINGLINE").length > 0),
            ],
            {
              success: "Anschrift und Anrede passen sich jetzt jedem Empfänger an.",
              failure: "Adressblock oder Grußzeile fehlen noch.",
            },
          );
        },
      },
    },
    {
      id: "vorschau",
      title: "Vorschau und Fehlerprüfung",
      explanation: [
        "„Vorschau Ergebnisse“ ersetzt die Felder durch die Werte des ersten Datensatzes; " +
          "mit den Pfeilen daneben blätterst du durch die Empfänger. So fallen fehlende " +
          "Leerzeichen, doppelte Anreden und zu lange Namen auf, bevor gedruckt wird.",
        "„Auf Fehler überprüfen“ spielt den Seriendruck einmal durch und meldet Felder, " +
          "die es in der Datenquelle nicht gibt. Und ein praktischer Tipp: Zahlen und " +
          "Beträge aus Excel erscheinen manchmal mit vielen Nachkommastellen. Dann gehört " +
          "ein Formatschalter an das Feld, etwa { MERGEFIELD Betrag \\# \"0,00 €\" }.",
      ],
      keyPoints: [
        "Vorschau Ergebnisse, dann mit den Pfeilen blättern.",
        "Auf Fehler überprüfen vor dem Druck.",
        "Formatschalter \\# für Zahlen, \\@ für Datumsangaben.",
      ],
    },
  ],
};
